// frontend/src/services/surveyService.js

import axiosInstance from "../axiosInstance";

// Fetch all surveys
export const fetchSurveys = async () => {
  try {
    const response = await axiosInstance.get('/surveys');
    return response.data; // Expected to return { surveys: [...] }
  } catch (error) {
    console.error('Error fetching surveys:', error.response?.data?.message || error.message);
    throw error;
  }
};

// Fetch survey by ID (includes questions)
export const fetchSurveyById = async (id) => {
  try {
    const response = await axiosInstance.get(`/surveys/${id}/details`);
    return response.data; // Expected to return { survey: {...} }
  } catch (error) {
    console.error('Error fetching survey by ID:', error.response?.data?.message || error.message);
    throw error;
  }
};

// Create a new survey
export const createSurvey = async (surveyData) => {
  try {
    const response = await axiosInstance.post('/surveys', surveyData);
    return response.data; // Expected to return { surveyId: "..." }
  } catch (error) {
    console.error('Error creating survey:', error.response?.data?.message || error.message);
    throw error;
  }
};

// Update an existing survey (including questions)
export const updateSurvey = async (id, surveyData) => {
  try {
    const response = await axiosInstance.put(`/surveys/${id}`, surveyData);
    return response.data; // Expected to return { survey: {...} }
  } catch (error) {
    console.error('Error updating survey:', error.response?.data?.message || error.message);
    throw error;
  }
};

// Delete a survey by ID
export const deleteSurvey = async (id) => {
  try {
    const response = await axiosInstance.delete(`/surveys/${id}`);
    return response.data; // Expected to return { message: "Survey deleted successfully." }
  } catch (error) {
    console.error('Error deleting survey:', error.response?.data?.message || error.message);
    throw error;
  }
};

// Fetch available survey types
export const fetchSurveyTypes = async () => {
  try {
    const response = await axiosInstance.get('/surveys/survey-types');
    return response.data.surveyTypes;
  } catch (error) {
    console.error('Error fetching survey types:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Edit a specific question in a survey.
 * @param {string} surveyId - ID of the survey.
 * @param {string} questionId - ID of the question.
 * @param {Object} updateData - Data to update (questionText, questionType, answerOptions, comment).
 * @returns {Promise<Object>} - Updated survey data.
 */
export const editQuestion = async (surveyId, questionId, updateData) => {
  try {
    const response = await axiosInstance.put(
      `/surveys/${surveyId}/questions/${questionId}/edit`,
      updateData
    );
    return response.data; // { success: true, data: survey }
  } catch (error) {
    console.error('Error editing question:', error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Remix a specific question in a survey.
 * @param {string} surveyId - ID of the survey.
 * @param {string} questionId - ID of the question.
 * @param {string} newQuestionText - Prompt or criteria for remixing the question.
 * @returns {Promise<Object>} - Updated survey data.
 */
export const remixQuestion = async (surveyId, questionId, newQuestionText) => {
  try {
    const response = await axiosInstance.put(
      `/surveys/${surveyId}/questions/${questionId}/remix`,
      { newQuestionText }
    );
    return response.data; // Expected to return { success: true, data: { survey, questions } }
  } catch (error) {
    console.error("Error remixing question:", error.response?.data?.message || error.message);
    throw error;
  }
};

/**
 * Add a new question to a survey section.
 * @param {string} surveyId - ID of the survey.
 * @param {number} sectionIndex - Index of the section where the question will be added.
 * @param {Object} questionData - Data for the new question.
 * @returns {Promise<Object>} - Updated survey data.
 */
export const addQuestion = async (surveyId, questionData) => {
  console.log("surveyService - Payload sent to backend:", questionData); // Debug log

  try {
    const response = await axiosInstance.post(`/surveys/${surveyId}/questions`, questionData);
    return response.data;
  } catch (error) {
    console.error("Error adding question:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Add a comment to a survey
export const addComment = async (token, questionId, commenterName, commentText) => {
  try {
    const response = await axiosInstance.post(`/surveys/shared/${token}/comments`, {
      questionId,
      commenterName,
      commentText,
    });
    return response.data;
  } catch (error) {
    console.error("Error adding comment:", error.response?.data?.message || error.message);
    throw error;
  }
};

// Fetch comments for a shared survey
export const fetchComments = async (token) => {
  try {
    const response = await axiosInstance.get(`/surveys/shared/${token}/comments`);
    return response.data.comments;
  } catch (error) {
    console.error("Error fetching comments:", error.response?.data?.message || error.message);
    throw error;
  }
};
